import React, { useState, useEffect } from "react";
import {
  FiFileText,
  FiUsers,
  FiCreditCard,
  FiBriefcase,
  FiCheckCircle,
} from "react-icons/fi";
import {
  getPayments,
  getArbitrationStats,
  getEfilingStats,
  getCustomerStats,
} from "../../../Services/Dashboard/Dashboard.js";
import axiosInstance from "../../../Middleware/AxiosInstanceInterceptor";
import Cookies from "universal-cookie";

const Dashboard = () => {
  const cookies = new Cookies();
  const token = cookies.get("authToken");
  // console.log(token,"token");

  const [stats, setStats] = useState({
    totalPayments: 0,
    totalPaymentAmount: 0,
    paymentThisMonth: 0,
    totalArbitrations: 0,
    arbitrationsThisMonth: 0,
    totalEfilings: 0,
    efilingsThisMonth: 0,
    totalCustomers: 0,
    customersThisMonth: 0,
    totalIndividuals: 0,
    individualsThisMonth: 0,
    totalOrganizations: 0,
    organizationsThisMonth: 0,
  });

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("");

  const fetchPayments = async () => {
    try {
      setLoading(true);

      const data = await getPayments(token, paymentStatus);

      setStats((prev) => ({
        ...prev,
        totalPayments: data.totalDocs || 0,
        totalPaymentAmount: data.totalAmount || 0,
      }));
    } catch (error) {
      console.error("Payment API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [paymentStatus, token]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        let arbitration = {};
        let efiling = {};
        let customers = {};

        try {
          arbitration = await getArbitrationStats(token);
          console.log("Arbitration API response:", arbitration);
        } catch (err) {
          console.log("Arbitration API not available");
        }

        try {
          efiling = await getEfilingStats(token);
          console.log("Efiling API response:", efiling);
        } catch (err) {
          console.log("Efiling API error", err);
        }

        try {
          customers = await getCustomerStats(token);
          console.log("Customer API response:", customers);
        } catch (err) {
          console.log("Customer API error", err);
        }

        setStats((prev) => ({
          ...prev,
          totalArbitrations: arbitration?.totalDocs || 0,
          arbitrationsThisMonth: arbitration?.thisMonth || 0,
          totalEfilings: efiling?.response?.totalDocs || 0,
          efilingsThisMonth: efiling?.thisMonth || 0,
          totalCustomers: customers?.response?.totalDocs || 0,
          totalIndividuals: customers?.response?.totalUsers || 0,
          totalOrganizations: customers?.response?.totalOrganizations || 0,
        }));
      } catch (error) {
        console.error("Dashboard API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const MetricCard = ({
    icon: Icon,
    title,
    value,
    growth,
    bgColor,
    children,
  }) => {
    return (
      <div className="bg-white rounded-xl shadow-sm mt-4 p-4 sm:p-5 lg:p-6 hover:shadow-md transition">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>

          <div className={`${bgColor} p-3 rounded-lg`}>
            <Icon size={20} className="text-white" />
          </div>
        </div>

        {/* Value OR Custom Content */}
        {children ? (
          children
        ) : (
          <h3 className="text-3xl font-bold text-gray-800">
            {loading ? "-" : value}
          </h3>
        )}

        {/* Footer */}
        <div className="flex items-center mt-4 text-sm">
          <span className="text-green-500 font-semibold">+{growth}</span>
          <span className="text-gray-400 ml-2">Since last month</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 ml-0 lg:ml-64 px-4 sm:px-6 lg:px-8 py-4 transition-all duration-300">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Dashboard Overview
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
            Welcome to Legal CMS Management System
          </p>
        </div>

        {/* Main Stats Grid - Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Payment Card */}
          <MetricCard
            icon={FiCreditCard}
            title="Total Payments"
            growth={stats.paymentThisMonth}
            bgColor="bg-blue-500"
          >
            <div className="flex flex-col mt-2">
              <h3 className="text-3xl font-bold text-gray-800">
                {loading ? "-" : stats.totalPayments}
              </h3>

              <p className="text-sm text-gray-500">
                ₹{stats.totalPaymentAmount} total amount
              </p>

              <div className="mt-2">
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="border rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm w-full sm:w-auto"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failure">Failure</option>
                </select>
              </div>
            </div>
          </MetricCard>

          {/* Arbitration Card */}
          <MetricCard
            icon={FiBriefcase}
            title="Total Arbitrations"
            value={stats.totalArbitrations}
            growth={stats.arbitrationsThisMonth}
            bgColor="bg-purple-500"
          />

          {/* eFiling Card */}
          <MetricCard
            icon={FiFileText}
            title="Total eFiling"
            value={stats.totalEfilings}
            growth={stats.efilingsThisMonth}
            bgColor="bg-orange-500"
          />
        </div>

        {/* Customer Management Section - Row 2 */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">
            Customer Management
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {/* Total Customers */}
            <MetricCard
              icon={FiUsers}
              title="Total Customers"
              value={stats.totalCustomers}
              growth={stats.customersThisMonth}
              bgColor="bg-green-500"
            />

            {/* Individual Customers */}
            <MetricCard
              icon={FiCheckCircle}
              title="Individual Customers"
              value={stats.totalIndividuals}
              growth={stats.individualsThisMonth}
              bgColor="bg-indigo-500"
            />

            {/* Organization Customers */}
            <MetricCard
              icon={FiUsers}
              title="Organization Customers"
              value={stats.totalOrganizations}
              growth={stats.organizationsThisMonth}
              bgColor="bg-pink-500"
            />
          </div>
        </div>

        {/* Quick Stats Summary */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <h2 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">
            Summary
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4">
            <div className="text-center py-2 md:py-3 border-r border-gray-200 last:border-r-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">Payment</p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalPayments}
              </p>
            </div>
            <div className="text-center py-2 md:py-3 border-r border-gray-200 last:border-r-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">
                Arbitration
              </p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalArbitrations}
              </p>
            </div>
            <div className="text-center py-2 md:py-3 border-r border-gray-200 last:border-r-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">eFiling</p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalEfilings}
              </p>
            </div>
            <div className="text-center py-2 md:py-3 border-r border-gray-200 last:border-r-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">
                Total Customers
              </p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalCustomers}
              </p>
            </div>
            <div className="text-center py-2 md:py-3 border-r border-gray-200 last:border-r-0">
              <p className="text-xs md:text-sm text-gray-600 mb-1">
                Individual
              </p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalIndividuals}
              </p>
            </div>
            <div className="text-center py-2 md:py-3">
              <p className="text-xs md:text-sm text-gray-600 mb-1">
                Organization
              </p>
              <p className="text-lg md:text-xl font-bold text-gray-800">
                {stats.totalOrganizations}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
